import type { ElementType } from 'react'
import { Icons } from '@/components/ui/icons'
import { coworkerCountWord } from './coworker-count'
import { COWORKERS } from './coworkers'

export const IS_PA_BUILD = process.env.NEXT_PUBLIC_BUILD_TARGET === 'pa-consulting'

/** Which live count a nav item shows as a badge (see hooks/useShellCounts). */
export type NavBadgeKey = 'tenders' | 'approvals' | 'supplyDesk'

/** What each badge counts, in a word a screen reader says after the number. */
export const BADGE_NOUN: Record<NavBadgeKey, string> = {
  tenders: 'live',
  approvals: 'waiting',
  supplyDesk: 'waiting',
}

export interface NavItem {
  href: string
  label: string
  icon: ElementType
  /** One sentence. Shown on hover and in the page info popover. */
  description: string
  adminOnly?: boolean
  /** Hidden in specific build targets (e.g. pa-consulting hides Invoices) */
  hidden?: boolean
  badgeKey?: NavBadgeKey
}

export interface NavSection {
  title: string
  items: NavItem[]
  /** Folded groups show only their title (with a chevron) until opened. */
  collapsedByDefault?: boolean
}

export const TENDER_EXPLORER_HREF = IS_PA_BUILD ? '/tender' : '/procurement'

/**
 * The rail, exactly as on screens s01 to s11:
 *
 *   COMMAND CENTER   Command Briefing, Dashboard and MI, Tender Explorer, My Approvals,
 *                    Contract Hub
 *   SUPPLY CHAIN     Supply Chain Desk, Orders and Deliveries
 *   AI INTELLIGENCE  AI Policies, AI Insights, Coworker Hub
 *   PLATFORM         Data Manager
 *   ADMINISTRATION   (folded) Start new process, Templates & Clauses, users, sessions,
 *                    audit logs, admin settings
 *   SYSTEM           (folded) Settings
 *
 * The mobile drawer, the command palette and the page info popovers all read
 * this one list, with the same role filter.
 */
export const navItems: NavSection[] = [
  {
    title: 'Command Center',
    items: [
      {
        href: '/',
        label: 'Command Briefing',
        icon: Icons.dashboard,
        description:
          'Your morning view: one line on what needs you, and a place to ask.',
      },
      {
        href: '/dashboard',
        label: 'Dashboard and MI',
        icon: Icons.barChart,
        description:
          'The procurement dashboard: the pipeline, its value and how decisions are going.',
      },
      {
        href: TENDER_EXPLORER_HREF,
        label: IS_PA_BUILD ? 'Tender Explorer' : 'Purchase Explorer',
        icon: Icons.inbox,
        badgeKey: 'tenders',
        description: IS_PA_BUILD
          ? 'Every live procurement, its coworker and its status, in one table.'
          : 'Browse purchase requests, track status, and manage your procurement pipeline.',
      },
      {
        href: '/procurement/approvals',
        label: 'My Approvals',
        icon: Icons.checkCircle,
        badgeKey: 'approvals',
        description:
          'Decisions waiting for you, oldest first, each naming who decides.',
      },
      {
        href: '/contracts',
        label: 'Contract Hub',
        icon: Icons.fileText,
        description:
          'Awarded contracts in Manage: KPIs, service credits and contracts nearing their end.',
      },
    ],
  },
  {
    title: 'Supply Chain',
    items: [
      {
        href: '/supply-chain',
        label: 'Supply Chain Desk',
        icon: Icons.messageCircle,
        badgeKey: 'supplyDesk',
        hidden: !IS_PA_BUILD,
        description:
          'Sophie answers requisitioners from Trust policy and the catalogue, and hands the rest to P2P.',
      },
      {
        href: '/supply-chain/orders',
        label: 'Orders and Deliveries',
        icon: Icons.truck,
        hidden: !IS_PA_BUILD,
        description:
          'Where each order is, site by site. Every status is tagged confirmed, supplier-reported or expected.',
      },
    ],
  },
  {
    title: 'AI Intelligence',
    items: [
      {
        href: '/ai/policies',
        label: 'AI Policies',
        icon: Icons.globe,
        description:
          'The rules your coworkers follow, in four sets: Procurement Act 2023, Provider Selection Regime, Trust Standing Financial Instructions and Supply chain policy.',
      },
      {
        href: '/ai/insights',
        label: 'AI Insights',
        icon: Icons.lightbulb,
        description:
          'Where time goes, which rules fire most, and changes your coworkers suggest.',
      },
      {
        href: '/agent-hub',
        label: 'Coworker Hub',
        icon: Icons.network,
        description:
          `Meet the ${coworkerCountWord(COWORKERS.length + 1)} coworkers, the skills each one uses, the systems they work in and the time they give back.`,
      },
    ],
  },
  {
    title: 'Platform',
    items: [
      {
        href: '/data-manager',
        label: 'Data Manager',
        icon: Icons.layers,
        description: 'The data your coworkers read, by source.',
      },
    ],
  },
  {
    title: 'Administration',
    collapsedByDefault: true,
    items: [
      {
        href: '/admin/start-process',
        label: 'Start new process',
        icon: Icons.plusCircle,
        adminOnly: true,
        hidden: !IS_PA_BUILD,
        description:
          'Begin a new procurement from a request, without waiting for an email.',
      },
      {
        href: '/contracts/templates',
        label: 'Templates & Clauses',
        icon: Icons.fileText,
        description:
          'Manage contract templates, standard clauses, and document stationery.',
      },
      {
        href: '/admin/users',
        label: 'Users and roles',
        icon: Icons.users,
        adminOnly: true,
        description:
          'Create and manage user accounts and access. Roles and groups are linked from this page.',
      },
      {
        href: '/admin/sessions',
        label: 'Sessions and sign-ins',
        icon: Icons.device,
        adminOnly: true,
        description:
          'View active user sessions and the sign-in history.',
      },
      {
        href: '/admin/audit',
        label: 'Audit logs',
        icon: Icons.scrollText,
        adminOnly: true,
        description: 'System-wide audit log of actions and changes.',
      },
      {
        href: '/admin/settings',
        label: 'Admin settings',
        icon: Icons.settings,
        adminOnly: true,
        description: 'Platform-wide configuration and administration.',
      },
    ],
  },
  {
    title: 'System',
    collapsedByDefault: true,
    items: [
      {
        href: '/settings',
        label: 'Settings',
        icon: Icons.settings,
        description:
          'Personal preferences, notifications, and account settings.',
      },
    ],
  },
]

/**
 * Pages that stay reachable and are not in the rail: palette "More pages" and
 * links inside other pages. Admin only unless stated. Nothing is deleted.
 */
export const morePages: NavItem[] = [
  {
    href: '/admin/roles',
    label: 'Role Management',
    icon: Icons.shield,
    adminOnly: true,
    description: 'Define roles and permission assignments.',
  },
  {
    href: '/admin/groups',
    label: 'Group Management',
    icon: Icons.network,
    adminOnly: true,
    description: 'Organize users into groups for access control.',
  },
  {
    href: '/admin/events',
    label: 'Login Events',
    icon: Icons.activity,
    adminOnly: true,
    description: 'Audit trail of user login activity.',
  },
  {
    href: '/workbench',
    label: 'Workbench',
    icon: Icons.workbench,
    adminOnly: true,
    description: 'The purchase request board.',
  },
  {
    href: '/vendors',
    label: 'Vendors',
    icon: Icons.building,
    adminOnly: true,
    description: 'Manage vendor profiles, contacts, and supplier relationships.',
  },
  {
    href: '/invoices',
    label: 'Invoices',
    icon: Icons.receipt,
    adminOnly: true,
    description: 'Track and process invoices linked to purchase orders and contracts.',
  },
  {
    href: '/brand',
    label: 'Brand and design',
    icon: Icons.palette,
    adminOnly: true,
    description: 'The brand reference for the product.',
  },
]

/** Roles that see admin items (the existing rule, now shared by rail, drawer and palette). */
export const ADMIN_ROLES = ['admin', 'pa-contract-manager'] as const

export function isAdminRoles(roles: readonly string[] | null | undefined): boolean {
  return Boolean(roles?.some((role) => (ADMIN_ROLES as readonly string[]).includes(role)))
}

/** The rail for a viewer: hidden items and admin items the viewer cannot see are removed. */
export function visibleNavSections(isAdmin: boolean): NavSection[] {
  return navItems
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.hidden && (!item.adminOnly || isAdmin)
      ),
    }))
    .filter((section) => section.items.length > 0)
}

/** More pages for a viewer (admin items only for admins). */
export function visibleMorePages(isAdmin: boolean): NavItem[] {
  return morePages.filter((item) => !item.adminOnly || isAdmin)
}

/** The section that holds an href, so a route inside a folded group opens it. */
export function sectionTitleForHref(href: string): string | undefined {
  return navItems.find((section) =>
    section.items.some((item) => item.href === href)
  )?.title
}

function allItems(): NavItem[] {
  return [...navItems.flatMap((section) => section.items), ...morePages]
}

/**
 * Nav item for a route: exact match first, then the longest parent route, so a
 * record such as `/tender/TND-NHS-004` falls back to Tender Explorer.
 */
export function getNavItemByHref(href: string): NavItem | undefined {
  const items = allItems()
  const exact = items.find((item) => item.href === href)
  if (exact) return exact
  const clean = href.split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  return items
    .filter((item) => item.href !== '/' && clean.startsWith(`${item.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0]
}
